import { useState, useCallback, useEffect, useRef } from 'react';
import type { SessionInfo } from '../types/gateway';
import type { ChatMessage } from '../types/foreman';
import { gateway } from '../gateway/connection';
import { parseForemanResponse } from '../gateway/parser';

export interface UseSessionsReturn {
  sessions: SessionInfo[];
  activeSessionId: string | null;
  createSession: (name?: string) => void;
  switchSession: (id: string) => void;
  loadingHistory: boolean;
  chatHistory: ChatMessage[];
}

interface OCSession {
  key: string;
  createdAt?: string;
  updatedAt?: number | string;
  metadata?: Record<string, unknown>;
}

interface OCHistoryMessage {
  role: string;
  content: string | { type: string; text?: string }[];
  timestamp?: number;
}

export function useSessions(): UseSessionsReturn {
  const [sessions, setSessions] = useState<SessionInfo[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>('main');
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const fetchedRef = useRef(false);

  // Fetch sessions when gateway connects
  useEffect(() => {
    const unsub = gateway.onStatusChange((status) => {
      if (status === 'connected' && !fetchedRef.current) {
        fetchedRef.current = true;
        fetchSessions();
      }
      if (status === 'disconnected') {
        fetchedRef.current = false;
      }
    });

    // If already connected, fetch immediately
    if (gateway.connected && !fetchedRef.current) {
      fetchedRef.current = true;
      fetchSessions();
    }

    return unsub;
  }, []);

  const fetchSessions = async () => {
    try {
      const result = await gateway.request('sessions.list', {}) as { sessions?: OCSession[] };
      const ocSessions = result?.sessions || [];

      const mapped: SessionInfo[] = ocSessions
        .filter((s) => s.key && s.key !== 'system')
        .map((s) => {
          // Keys come as "agent:main:main" — extract the user-facing part
          const shortKey = extractSessionKey(s.key);
          return {
            id: shortKey,
            name: formatSessionName(shortKey),
            createdAt: s.createdAt || new Date().toISOString(),
            lastMessageAt: s.updatedAt ? new Date(s.updatedAt).toISOString() : (s.createdAt || new Date().toISOString()),
            metadata: s.metadata,
          };
        });

      // Deduplicate by id (in case multiple agent prefixes map to same short key)
      const seen = new Set<string>();
      const deduped = mapped.filter((s) => {
        if (seen.has(s.id)) return false;
        seen.add(s.id);
        return true;
      });

      // Ensure "main" is always present
      if (!deduped.find((s) => s.id === 'main')) {
        deduped.unshift({
          id: 'main',
          name: 'Main',
          createdAt: new Date().toISOString(),
          lastMessageAt: new Date().toISOString(),
        });
      }

      // Sort by most recent first
      deduped.sort((a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime());

      setSessions(deduped);
    } catch (err) {
      console.error('[useSessions] Failed to fetch sessions:', err);
      // Fallback: at least show main
      setSessions([{
        id: 'main',
        name: 'Main',
        createdAt: new Date().toISOString(),
        lastMessageAt: new Date().toISOString(),
      }]);
    }
  };

  // Load history when session changes
  useEffect(() => {
    if (!activeSessionId) return;
    loadHistory(activeSessionId);
  }, [activeSessionId]);

  const loadHistory = async (sessionKey: string) => {
    if (!gateway.connected) {
      setChatHistory([]);
      return;
    }

    setLoadingHistory(true);
    try {
      const result = await gateway.request('chat.history', {
        sessionKey,
        limit: 100,
      }) as { messages?: OCHistoryMessage[] };

      const messages = result?.messages || [];
      const mapped: ChatMessage[] = messages.map((m, i) => {
        const text = extractHistoryText(m);
        const isUser = m.role === 'user' || m.role === 'human';
        return {
          id: `hist-${sessionKey}-${i}`,
          type: isUser ? 'user' : 'foreman',
          content: text,
          parsed: isUser ? undefined : parseForemanResponse(text),
          sender: isUser ? 'You' : 'Foreman',
          timestamp: m.timestamp ? new Date(m.timestamp).toISOString() : new Date().toISOString(),
          sessionId: sessionKey,
        };
      });

      setChatHistory(mapped);
    } catch (err) {
      console.error('[useSessions] Failed to load history:', err);
      setChatHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const createSession = useCallback((name?: string) => {
    const key = name || `thread-${Date.now().toString(36)}`;
    const newSession: SessionInfo = {
      id: key,
      name: formatSessionName(key),
      createdAt: new Date().toISOString(),
      lastMessageAt: new Date().toISOString(),
    };
    setSessions((prev) => [newSession, ...prev]);
    setActiveSessionId(key);
    setChatHistory([]);
  }, []);

  const switchSession = useCallback((id: string) => {
    setActiveSessionId(id);
  }, []);

  return {
    sessions,
    activeSessionId,
    createSession,
    switchSession,
    loadingHistory,
    chatHistory,
  };
}

/** Extract user-facing session key from full internal key
 *  e.g. "agent:main:main" → "main", "agent:main:thread-abc" → "thread-abc" */
function extractSessionKey(fullKey: string): string {
  // Pattern: agent:{agentId}:{sessionKey}
  const parts = fullKey.split(':');
  if (parts.length >= 3 && parts[0] === 'agent') {
    return parts.slice(2).join(':');
  }
  return fullKey;
}

function formatSessionName(key: string): string {
  if (key === 'main') return 'Main';
  // Remove prefixes like "holler:" or "thread-"
  const cleaned = key.replace(/^(holler:|thread-)/, '');
  // Capitalize first letter
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

function extractHistoryText(msg: OCHistoryMessage): string {
  if (typeof msg.content === 'string') return msg.content;
  if (Array.isArray(msg.content)) {
    return msg.content
      .filter((p) => p.type === 'text')
      .map((p) => (p as { text: string }).text)
      .join('');
  }
  return '';
}
